import React, { useEffect, useState } from "react";
import * as yup from "yup";
import { useFormik } from "formik";
import BaseTextField from "../../components/ui/BaseTextField";
import BaseButton from "../../components/ui/BaseButton";
import { useMutation } from "@tanstack/react-query";
import { login } from "../../services/api";
import { useDispatch } from "react-redux";
import { setLoginSuccessful } from "../../store/authUser/authUserSlice";
import BaseCheckbox from "../../components/ui/BaseCheckbox";
import { ReactComponent as GoogleIcon } from "../../assets/global-icons/download-google.svg";
import { ReactComponent as AppleIcon } from "../../assets/global-icons/download-apple.svg";
import { showToast } from "../../store/toast/toastSlice";
import { Link, useNavigate } from "react-router-dom";
import BaseConfirmModal from "../../components/ui/BaseConfirmModal";
// Social login temporarily disabled.
// import GoogleSignInButton from "../../components/ui/GoogleSignInButton";
// import AppleSignInButton from "../../components/ui/AppleSignInButton";

/** Reasons the OAuth callback can bounce someone back here, in plain English. */
const SOCIAL_ERROR_MESSAGES = {
  NO_ACCOUNT: (email) => (
    <>
      No account found for {email ? <span className="font-medium">{email}</span> : "that account"}.
      Please sign up or use a different account.
    </>
  ),
  NO_EMAIL: () =>
    "We couldn’t get an email address from that account. Please sign in with your email instead.",
  UNVERIFIED_EMAIL: () =>
    "That account’s email isn’t verified. Please sign in with your password first.",
  ACCOUNT_DELETED: () => "This account has been deleted. Please contact support.",
  ACCOUNT_DEACTIVATED: () => "Your account is not active. Please contact support.",
  PROVIDER_ERROR: () => "We couldn’t complete sign-in. Please try again.",
};

const validationSchema = yup.object({
  email: yup.string("Enter your email").email("Enter a valid email").required("Email is required"),
  password: yup
    .string("Enter your password")
    // .min(8, "Password should be of minimum 8 characters length")
    .required("Password is required"),
});

const LoginForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isShowPassword, setIsShowPassword] = useState(false);
  const [rememberChecked, setRememberChecked] = useState(false);
  const [isDeactivatedModalOpen, setIsDeactivatedModalOpen] = useState(false);
  // A customer's user is activated by its company, so it gets no support link.
  const [isCustomerUser, setIsCustomerUser] = useState(false);
  const [socialError, setSocialError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reason = params.get("socialError");
    if (!reason) return;

    setSocialError({ reason, email: params.get("email") });
    window.history.replaceState({}, document.title, window.location.pathname);
  }, []);

  const {
    isPending: isLoginLoading,
    status,
    error: isLoginError,
    mutateAsync: loginMutation,
  } = useMutation({
    mutationFn: login,
    onSuccess: (data) => {
      const payload = {
        user: data.data,
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
      };

      dispatch(
        showToast({
          message: "Login Successfully",
          type: "success",
        }),
      );
      dispatch(setLoginSuccessful(payload));
    },
  });

  /*  if (error) {
    dispatch(
      showToast({
        message: error.response.data.message,
        type: "error",
      })
    );
  } */

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      handleSubmit(values);
    },
    validateOnChange: false,
    validateOnBlur: false,
  });

  const handleSubmit = async (values) => {
    try {
      await loginMutation(values);
    } catch (error) {
      const isDeactivated = error?.response?.data?.deactivated;

      if (isDeactivated) {
        setIsCustomerUser(!!error?.response?.data?.isCustomerUser);
        setIsDeactivatedModalOpen(true);
        return;
      }
      if (error?.response?.status === 307) {
        const email = encodeURIComponent(formik.values.email);

        navigate(`/otp?email=${email}`);
      }
      dispatch(
        showToast({
          message: error.response?.data?.message || "An error occurred",
          type: "error",
        }),
      );
    }
  };

  return (
    <form style={{ width: "100%" }}>
      {isLoginError && (
        <div className="flex items-center mb-6 bg-red-100 py-2 px-4 rounded-md">
          <i className="fa-solid fa-circle-exclamation text-red-600"></i>
          <p className="text-base text-red-600 ml-2">Invalid Email/Password</p>
        </div>
      )}
      {socialError && (
        <div className="flex items-start mb-6 bg-red-100 py-3 px-4 rounded-md">
          <i className="fa-solid fa-circle-exclamation text-red-600 mt-[5px]"></i>
          <p className="text-base text-red-600 ml-2">
            {(SOCIAL_ERROR_MESSAGES[socialError.reason] ?? SOCIAL_ERROR_MESSAGES.PROVIDER_ERROR)(
              socialError.email,
            )}
            {/* Only ever set for the generic failure, where the underlying code is the
                one thing that says which piece of the setup is wrong. */}
            {socialError.detail && (
              <span className="block text-xs opacity-70 mt-1">{socialError.detail}</span>
            )}
          </p>
        </div>
      )}
      <BaseTextField
        height="60px"
        className="mb-6"
        InputLabelProps={{ shrink: true }}
        endAdornment={<i class="fa-regular fa-envelope text-base"></i>}
        id="email"
        placeholder="William123@gmail.com"
        name="email"
        label="Email"
        value={formik.values.email}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={(formik.touched.email && Boolean(formik.errors.email)) || isLoginError}
        helperText={formik.touched.email && formik.errors.email}
      />

      <BaseTextField
        InputLabelProps={{ shrink: true }}
        endAdornment={
          <i
            class={`fa-solid ${
              isShowPassword ? "fa-eye" : "fa-eye-slash"
            } text-base cursor-pointer`}
            onClick={() => setIsShowPassword(!isShowPassword)}
          ></i>
        }
        placeholder="*****************"
        height="60px"
        id="password"
        name="password"
        label="Password"
        type={isShowPassword ? "text" : "password"}
        value={formik.values.password}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={(formik.touched.password && Boolean(formik.errors.password)) || isLoginError}
        helperText={formik.touched.password && formik.errors.password}
      />

      <div className="d-flex items-center content-between remember-fw-wrapper mb-3 mt-6">
        <div className="d-flex align-items-center">
          <BaseCheckbox
            style={{ marginRight: "0" }}
            checked={rememberChecked}
            onChange={(e) => setRememberChecked(e.target.checked)}
          />
          <p className={rememberChecked ? "text-primary" : "text-[#525354]"}>Remember Me</p>
        </div>
        <Link to="/forgotPassword">
          <p className="forgot-password text-primary">Forgot Password?</p>
        </Link>
      </div>

      <BaseButton
        fullWidth
        color="primary"
        loaderSize="24px"
        isLoading={isLoginLoading}
        className="rounded-full text-lg py-[6px] mb-6"
        variant="contained"
        type="submit"
        onClick={formik.handleSubmit}
      >
        Login
      </BaseButton>

      {/* Social login temporarily disabled.
      <p className="separator text-base mb-6">
        <span className="line"></span>
        <span className="or-text"> or </span>
        <span className="line"></span>
      </p>

      <GoogleSignInButton className="mb-6" />

      Apple answers in a response rather than a redirect, so it reports failures
      straight into the same banner state the Google callback fills from the URL.
      <AppleSignInButton className="mb-6" onError={setSocialError} />
      */}

      <p className="text-base text-center">
        Don’t have Login ?{" "}
        {/* When Google turned up no account, this is the control they need next — so
            spotlight the one that already exists rather than adding another. */}
        <Link
          className={`text-primary text-base font-medium inline-block rounded-full px-2 ${
            socialError?.reason === "NO_ACCOUNT" ? "bg-[#D8EBF8] animate-signupCue" : ""
          }`}
          to="/getStarted"
        >
          SignUp
        </Link>{" "}
        here
      </p>
      <p className="separator text-base">
        <span className="line"></span>
        <span className="or-text"> Never Miss a Job Update</span>
        <span className="line"></span>
      </p>

      <p className="text-base text-center">
        Engineers &amp; Providers — Get the Tech Domain App
      </p>
      <div className="flex justify-between px-11 mt-8">
        <a
          href="https://apps.apple.com/us/app/tech-domain/id6742741677"
          target="_blank"
          rel="noopener noreferrer"
        >
          <AppleIcon />
        </a>
        <a
          href="https://play.google.com/store/apps/details?id=com.techdomain.tdm&pli=1"
          target="_blank"
          rel="noopener noreferrer"
        >
          <GoogleIcon />
        </a>
      </div>
      {isDeactivatedModalOpen && isCustomerUser && (
        <BaseConfirmModal
          open={isDeactivatedModalOpen}
          onClose={() => setIsDeactivatedModalOpen(false)}
          onConfirm={() => setIsDeactivatedModalOpen(false)}
          title="Account Deactivated"
          description={
            <>
              <span className="block">
                Your access to your company&apos;s Tech Domain account has been deactivated by your
                organisation&apos;s administrator.
              </span>
              <span className="block mt-2">
                If you need access restored, please contact your company administrator.
              </span>
            </>
          }
          confirmText="OK"
          hideCancel
        />
      )}
      {isDeactivatedModalOpen && !isCustomerUser && (
        <BaseConfirmModal
          open={isDeactivatedModalOpen}
          onClose={() => setIsDeactivatedModalOpen(false)}
          onConfirm={() => {
            window.open(
              `mailto:helpdesk@techdomain.com.au?subject=Account Activation Request&body=My account (${formik.values.email}) is deactivated. Please assist.`,
            );
          }}
          title="Account Deactivated"
          description={
            <p>Your account is not active. Please contact support to reactivate your account.</p>
          }
          confirmText="Contact Support"
        />
      )}
    </form>
  );
};

export default LoginForm;
